import { Button, Grid } from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import { startCase } from "lodash";
import {
  ADD_DRUG_CODE_CATALOG_INITIAL_VALUES,
  DrugCodeCatalogFormType,
} from "../../../../../constants/initial-schemas";
import CustomSelect from "../../../../CustomSelect";
import Input from "../../../../Input";
import { DisclosureHost } from "../../../../../hooks/use-disclosure";
import SideSheetDrawer from "../../../../SidesheetDrawer";
import { MedicalCodeFormTypeEnum } from "../../../../../services";
import { useAddCataLogCode } from "../../../../../queries/catalog/use-add-catalog-code";
import { useUpdateCataLogCode } from "../../../../../queries/catalog/use-update-catalog-code";
import { useQueryClient } from "@tanstack/react-query";
import { useSnackbar } from "../../../../../hooks/useSnackbar";
import {
  getSuccessSnackbar,
  getErrorSnackbar,
} from "../../../../../utils/utils";

interface DrugCatalogDialogProps {
  uuid?: string;
  dialogTitle: string;
  disclosureHost: DisclosureHost;
  initialValues?: DrugCodeCatalogFormType;
}

const drugCodeCatalogValidationSchema = Yup.object({
  drug_id: Yup.string().trim(),
  form_type: Yup.string().trim(),
  drugName: Yup.string().trim().required("Drug name is required"),
  strength: Yup.string().trim(),
  description: Yup.string().trim(),
});

const DrugCatalogDrawer = ({
  uuid,
  dialogTitle,
  initialValues,
  disclosureHost,
}: DrugCatalogDialogProps) => {
  const {
    values,
    touched,
    errors,
    handleBlur,
    handleChange,
    handleSubmit,
    setFieldValue,
    resetForm,
  } = useFormik({
    initialValues: initialValues ?? ADD_DRUG_CODE_CATALOG_INITIAL_VALUES,
    validationSchema: drugCodeCatalogValidationSchema,
    enableReinitialize: true,
    onSubmit,
  });
  const { mutateAsync: addDrugCode } = useAddCataLogCode();
  const { mutateAsync: editDrugCode } = useUpdateCataLogCode();
  const queryClient = useQueryClient();
  const showSnackbar = useSnackbar();
  function onSubmit(values: DrugCodeCatalogFormType) {
    const transformedValues = {
      code: values.drugId,
      strength: values.strength,
      drugName: values.drugName,
      formType: values.formType,
      description: values.description,
    };

    (initialValues
      ? editDrugCode({
          values: { ...transformedValues, uuid, type: "DRUG_CATALOG" },
        })
      : addDrugCode({ values: { ...transformedValues, type: "DRUG_CATALOG" } })
    )
      .then((res) => {
        showSnackbar(getSuccessSnackbar(res));

        queryClient.invalidateQueries({
          queryKey: ["catalog-diagnosis-code-import"],
        });

        resetAndClose();
      })
      .catch((err) => showSnackbar(getErrorSnackbar(err)));
  }

  function resetAndClose() {
    resetForm();
    disclosureHost.close();
  }

  return (
    <SideSheetDrawer
      open={disclosureHost.opened}
      onClose={disclosureHost.close}
      title={dialogTitle}
      footerActions={
        <Button
          sx={{ width: "10em" }}
          onClick={() => handleSubmit()}
          size="large"
          variant="contained"
        >
          {dialogTitle?.includes("Edit") ? "Edit" : "Add"}
        </Button>
      }
    >
      <Grid
        container
        spacing={2}
        sx={{
          width: "100%",
        }}
      >
        <Grid container spacing={"20px"}>
          <Grid
            size={{
              sm: 6,
              lg: 4,
            }}
          >
            <Input
              label="Drug Code"
              textFieldProps={{
                name: "drugId",
                value: values.drugId,
                onChange: handleChange,
                onBlur: handleBlur,
                placeholder: "Enter Drug Code",
              }}
              error={touched.drugId && errors.drugId}
            />
          </Grid>
          <Grid
            size={{
              sm: 6,
              lg: 4,
            }}
          >
            <CustomSelect
              label="Form Type"
              options={Object.entries(MedicalCodeFormTypeEnum).map(
                ([key, value]) => ({
                  value: value,
                  label: startCase(key),
                })
              )}
              value={values.formType}
              onChange={(option: string | null) => {
                setFieldValue("formType", option);
              }}
              error={touched.formType && errors.formType}
            />{" "}
          </Grid>
          <Grid
            size={{
              sm: 6,
              lg: 4,
            }}
          >
            <Input
              required
              label="Drug Name"
              textFieldProps={{
                name: "drugName",
                value: values.drugName,
                onChange: handleChange,
                onBlur: handleBlur,
                placeholder: "Enter Drug Name",
              }}
              error={touched.drugName && errors.drugName}
            />
          </Grid>
          <Grid
            size={{
              sm: 6,
              lg: 4,
            }}
          >
            <Input
              label="Strength"
              textFieldProps={{
                name: "strength",
                value: values.strength,
                onChange: handleChange,
                onBlur: handleBlur,
                placeholder: "Enter Strength",
              }}
              error={touched.strength && errors.strength}
            />
          </Grid>
        </Grid>
        <Grid
          container
          sx={{
            gap: "0.275rem",
            flexDirection: "column",
          }}
        >
          <Input
            label="Description"
            textFieldProps={{
              name: "description",
              value: values.description,
              onChange: handleChange,
              onBlur: handleBlur,
              placeholder: "Description",
              rows: 3,
              multiline: true,
            }}
            error={touched.description && errors.description}
          />
        </Grid>
      </Grid>
    </SideSheetDrawer>
  );
};
export default DrugCatalogDrawer;
